/**
 * Custody of the offline-attendance data key
 * (`specs/offline-attendance-v1/implementation-plan.md`, decision D5, task FE-3).
 *
 * One AES-GCM-256 key per installation seals every offline-attendance row
 * (`sealed.ts`). Where it lives depends on the platform:
 *
 *   - WEB (`webcrypto`): a NON-extractable WebCrypto key, generated in the
 *     browser and stored as a `CryptoKey` in IndexedDB `meta.data_key` with
 *     its 16-character `keyId`. Script can use it but never read its bytes.
 *     At-rest protection only — code on the same origin can use it too.
 *   - NATIVE (`native`, `Capacitor.isNativePlatform()`): 32 random bytes kept
 *     in the Android Keystore / iOS Keychain through
 *     `services/device/secure-storage.service.ts`, base64url-encoded, and
 *     imported non-extractable on load; the raw bytes are zeroed once
 *     imported. Its `keyId` is the key's fingerprint — the first 16 base64url
 *     characters of SHA-256(raw). A shell without the plugin is
 *     `native_plugin_missing`: there is NO fallback to a web key or to
 *     `localStorage`.
 *
 * Either way `meta.data_key_marker = { keyId, custody }` is what tells a FIRST
 * RUN (no marker: a key may be created) from KEY LOSS (marker present, key
 * missing, different or unreadable as a key: `lost`). A lost key is never
 * replaced silently — every row sealed under it would be abandoned; recovery
 * is an explicit, separate flow. A key is only ever reported `ready` once its
 * marker vouches for it and it has encrypted and decrypted a probe.
 *
 * Races. Within one JS context calls are serialized (one queue). Across tabs
 * the web key is created check-and-set in ONE readwrite IndexedDB transaction
 * and then re-read, so two tabs on first run end up with the one key that was
 * stored — never two. On native the key is written to the keystore BEFORE its
 * marker: a crash between the two leaves an unmarked key that the next run
 * adopts, never a marker without a key.
 *
 * `loadDataKey()` never throws. Only a `ready` result is cached (per JS
 * context, and only while the connection it was read over lasts — see below);
 * `lost` and `unavailable` are re-evaluated on every call.
 *
 * A cached key is dropped the moment the offline database's connection
 * generation moves (`offlineDbGeneration()`, `db.ts`): another tab deleted the
 * database, the browser cleared it, or the connection simply ended. Otherwise a
 * key the old database vouched for would keep sealing rows into a NEW, empty
 * database whose next run mints a different key — punches the UI reported as
 * saved, unreadable for good. The next call re-reads the key (a cheap read when
 * nothing was lost). On the native shell the keystore outlives the database:
 * the same key is adopted again (with `create`). The repository's commit check
 * (`attendance-storage.ts`) refuses a capture whose key the database's marker
 * does not name, for a caller still holding the old key.
 */
import { Capacitor } from '@capacitor/core';
import { secureStorage } from '@/services/device/secure-storage.service';
import {
  OFFLINE_META_KEYS,
  isDataKeyId,
  isIndexedDbAvailable,
  offlineDb,
  offlineDbGeneration,
  parseDataKeyMarker,
  type DataKeyMarker,
  type OfflineMetaRow,
  type SyntryOfflineDb,
} from '@/services/storage/db';
import type { SealingKey } from '@/services/storage/sealed';
import { IS_DEV } from '@/utils/env';

/** Where the data key is kept. */
export type DataKeyCustody = DataKeyMarker['custody'];

export type DataKeyState =
  | { status: 'ready'; key: CryptoKey; keyId: string; custody: DataKeyCustody }
  | { status: 'lost'; custody: DataKeyCustody }
  | { status: 'unavailable'; reason: 'no_crypto' | 'no_storage' | 'native_plugin_missing' | 'error' };

/**
 * The key as the storage and sealing functions take it. A `{ status: 'ready' }`
 * state is assignable to it as-is.
 */
export type DataKeyReady = SealingKey;

/** The secure-storage entry that holds the native raw key (base64url, 43 characters). */
export const NATIVE_DATA_KEY_NAME = 'syntry.offline.data_key.v1';

type ReadyState = Extract<DataKeyState, { status: 'ready' }>;
type UnavailableReason = Extract<DataKeyState, { status: 'unavailable' }>['reason'];

/** A stored native key: 32 bytes as unpadded base64url. */
const NATIVE_RAW_KEY_RE = /^[A-Za-z0-9_-]{43}$/;
const RAW_KEY_BYTES = 32;
const KEY_ID_RANDOM_BYTES = 12;
/** AAD of the self-test every key passes before it is handed out (never used for data). */
const KEY_CHECK_AAD = 'syntry-offline|v1|key-check';

/* ── Public API ────────────────────────────────────────────────────────── */

/** The ready key, and the database connection generation it was read over (`offlineDbGeneration()`). */
let cached: { state: ReadyState; generation: number } | null = null;
/** Serializes calls within this JS context (the tail of the queue). */
let tail: Promise<void> = Promise.resolve();

/**
 * Resolve the installation's data key.
 *
 * `create: true` (default `false`) allows first-run creation — a new key, or a
 * marker for a stored key that has none. Without it a device with no key is
 * `unavailable/no_storage` and nothing is written. Never throws.
 *
 * A `ready` result is cached for this JS context while the offline database's
 * connection lasts: once its generation moves (the database was deleted,
 * cleared, re-created, or the connection merely ended) the next call reads the
 * key again instead of handing out one the database may no longer vouch for.
 * The cache is keyed by the generation the read BEGAN over, and generations
 * only move forward — so a result resolved while the generation moved is
 * returned, but the next call never serves it.
 */
export async function loadDataKey(opts?: { create?: boolean }): Promise<DataKeyState> {
  const create = opts?.create === true;
  const previous = tail;
  let release: () => void = () => undefined;
  tail = new Promise<void>((resolve) => {
    release = resolve;
  });
  try {
    await previous;
    const generation = offlineDbGeneration();
    if (cached !== null) {
      if (cached.generation === generation) return cached.state;
      // The connection the key was read over ended — perhaps with the database.
      cached = null;
    }
    const state = await resolveDataKey(create);
    // Under the generation the read began over: had it moved meanwhile, the
    // next call finds a newer one and reads the key again.
    if (state.status === 'ready') cached = { state, generation };
    return state;
  } finally {
    release();
  }
}

/** Forget the cached key (and any queued call) — a page reload, for specs. */
export function resetDataKeyCacheForTests(): void {
  cached = null;
  tail = Promise.resolve();
}

/* ── Resolution ────────────────────────────────────────────────────────── */

async function resolveDataKey(create: boolean): Promise<DataKeyState> {
  if (!hasWebCrypto()) return unavailable('no_crypto');
  try {
    return Capacitor.isNativePlatform() ? await resolveNative(create) : await resolveWeb(create);
  } catch (err) {
    // IndexedDB or WebCrypto failed in a way the steps above do not classify:
    // report it and keep nothing — failures are never cached, the next call retries.
    const name = errorName(err);
    if (IS_DEV) console.warn('[offline-keys] could not load the data key', name);
    return unavailable(name === 'MissingAPIError' ? 'no_storage' : 'error');
  }
}

/* ── Web custody ───────────────────────────────────────────────────────── */

type WebStored =
  | { kind: 'ready'; keyId: string; key: CryptoKey }
  | { kind: 'lost'; custody: DataKeyCustody }
  | { kind: 'unmarked'; keyId: string; key: CryptoKey } // a usable key no marker vouches for
  | { kind: 'empty' }; // no marker and no usable key

async function resolveWeb(create: boolean): Promise<DataKeyState> {
  if (!isIndexedDbAvailable()) return unavailable('no_storage');
  const db = offlineDb();
  let found = await readWeb(db);

  if (found.kind === 'empty' || found.kind === 'unmarked') {
    if (!create) return unavailable('no_storage');
    // Generated BEFORE the transaction: only Dexie operations are awaited inside one.
    const candidate = found.kind === 'empty' ? await newWebKey() : null;
    await db.transaction('rw', db.meta, async () => {
      // Check-and-set: another tab may have created (or vouched for) a key since our read.
      const current = classifyWeb(await db.meta.bulkGet([OFFLINE_META_KEYS.dataKey, OFFLINE_META_KEYS.dataKeyMarker]));
      if (current.kind === 'empty' && candidate) {
        await db.meta.bulkPut([
          { key: OFFLINE_META_KEYS.dataKey, value: { keyId: candidate.keyId, key: candidate.key } },
          { key: OFFLINE_META_KEYS.dataKeyMarker, value: markerFor(candidate.keyId, 'webcrypto') },
        ]);
      } else if (current.kind === 'unmarked') {
        await db.meta.put({ key: OFFLINE_META_KEYS.dataKeyMarker, value: markerFor(current.keyId, 'webcrypto') });
      }
      // 'ready' / 'lost': someone else got there first — their rows stay untouched.
    });
    // Verify what is ACTUALLY stored: after a race this is the other tab's key.
    found = await readWeb(db);
  }

  switch (found.kind) {
    case 'ready':
      return (await keyWorks(found.key)) ? ready(found.key, found.keyId, 'webcrypto') : unavailable('error');
    case 'lost':
      return { status: 'lost', custody: found.custody };
    default:
      // Written, yet not read back as a vouched-for key.
      return unavailable('error');
  }
}

async function readWeb(db: SyntryOfflineDb): Promise<WebStored> {
  const rows = await db.transaction('r', db.meta, () =>
    db.meta.bulkGet([OFFLINE_META_KEYS.dataKey, OFFLINE_META_KEYS.dataKeyMarker]),
  );
  return classifyWeb(rows);
}

function classifyWeb(rows: ReadonlyArray<OfflineMetaRow | undefined>): WebStored {
  const [keyRow, markerRow] = rows;
  const stored = keyRow ? parseWebKey(keyRow.value) : null;
  if (markerRow === undefined) {
    return stored ? { kind: 'unmarked', ...stored } : { kind: 'empty' };
  }
  const marker = parseDataKeyMarker(markerRow.value);
  // An unreadable marker still says a key existed: never treat it as a first run.
  if (!marker) return { kind: 'lost', custody: 'webcrypto' };
  if (marker.custody !== 'webcrypto') return { kind: 'lost', custody: marker.custody };
  if (!stored || stored.keyId !== marker.keyId) return { kind: 'lost', custody: 'webcrypto' };
  return { kind: 'ready', ...stored };
}

async function newWebKey(): Promise<{ keyId: string; key: CryptoKey }> {
  const key = await crypto.subtle.generateKey({ name: 'AES-GCM', length: 256 }, false, ['encrypt', 'decrypt']);
  return { keyId: bytesToBase64Url(crypto.getRandomValues(new Uint8Array(KEY_ID_RANDOM_BYTES))), key };
}

function parseWebKey(value: unknown): { keyId: string; key: CryptoKey } | null {
  if (typeof value !== 'object' || value === null) return null;
  const keyId: unknown = Reflect.get(value, 'keyId');
  const key: unknown = Reflect.get(value, 'key');
  if (!isDataKeyId(keyId) || !isAesGcmKey(key)) return null;
  return { keyId, key };
}

/** A secret AES-GCM CryptoKey allowed to encrypt and decrypt (duck-typed: it came out of a structured clone). */
function isAesGcmKey(value: unknown): value is CryptoKey {
  if (typeof value !== 'object' || value === null) return false;
  const algorithm: unknown = Reflect.get(value, 'algorithm');
  const usages: unknown = Reflect.get(value, 'usages');
  return (
    Reflect.get(value, 'type') === 'secret' &&
    typeof algorithm === 'object' &&
    algorithm !== null &&
    Reflect.get(algorithm, 'name') === 'AES-GCM' &&
    Array.isArray(usages) &&
    usages.includes('encrypt') &&
    usages.includes('decrypt')
  );
}

/* ── Native custody ────────────────────────────────────────────────────── */

type NativeStored =
  | { kind: 'ready'; raw: Uint8Array; keyId: string }
  | { kind: 'lost'; custody: DataKeyCustody }
  | { kind: 'unmarked'; raw: Uint8Array; keyId: string } // a valid stored key with no marker
  | { kind: 'empty' }; // no marker and no valid stored key

/** The keystore entry, decoded: raw bytes, `'malformed'` (present but not a key), or `null` (absent). */
type NativeRawRead = Uint8Array | 'malformed' | null;

async function resolveNative(create: boolean): Promise<DataKeyState> {
  if (!secureStorage.isSupported()) return unavailable('native_plugin_missing');
  // The marker lives in IndexedDB: without it a key could never be told from a lost one.
  if (!isIndexedDbAvailable()) return unavailable('no_storage');
  const db = offlineDb();

  const markerRow = await readMarkerRow(db);
  const read = await secureStorage.getString(NATIVE_DATA_KEY_NAME);
  // Unreadable is not absent: never conclude "lost", never create over it.
  if (!read.ok) return unavailable('error');

  const owned: Uint8Array[] = []; // every raw key buffer this call touched — zeroed on the way out
  try {
    const stored: NativeRawRead = read.value === null ? null : (decodeRawKey(read.value) ?? 'malformed');
    if (stored instanceof Uint8Array) owned.push(stored);
    let found = await classifyNative(markerRow, stored);

    if (found.kind === 'empty' || found.kind === 'unmarked') {
      if (!create) return unavailable('no_storage');
      let raw: Uint8Array;
      if (found.kind === 'empty') {
        raw = crypto.getRandomValues(new Uint8Array(RAW_KEY_BYTES));
        owned.push(raw);
        const encoded = bytesToBase64Url(raw);
        if (!(await secureStorage.setString(NATIVE_DATA_KEY_NAME, encoded))) return unavailable('error');
        // Read back: only a key the keystore really returns may get a marker.
        const back = await secureStorage.getString(NATIVE_DATA_KEY_NAME);
        if (!back.ok || back.value !== encoded) return unavailable('error');
      } else {
        raw = found.raw; // adopt: a crash after the keystore write and before the marker
      }
      const keyId = await fingerprint(raw);
      await db.transaction('rw', db.meta, async () => {
        const current = await db.meta.get(OFFLINE_META_KEYS.dataKeyMarker);
        if (current === undefined) {
          await db.meta.put({ key: OFFLINE_META_KEYS.dataKeyMarker, value: markerFor(keyId, 'native') });
        }
      });
      found = await classifyNative(await readMarkerRow(db), raw);
    }

    if (found.kind === 'lost') return { status: 'lost', custody: found.custody };
    if (found.kind !== 'ready') return unavailable('error');
    const key = await crypto.subtle.importKey('raw', found.raw, { name: 'AES-GCM' }, false, ['encrypt', 'decrypt']);
    if (!(await keyWorks(key))) return unavailable('error');
    return ready(key, found.keyId, 'native');
  } finally {
    // The CryptoKey holds its own copy; leave no raw key material behind in this heap.
    for (const bytes of owned) bytes.fill(0);
  }
}

async function readMarkerRow(db: SyntryOfflineDb): Promise<OfflineMetaRow | undefined> {
  return db.transaction('r', db.meta, () => db.meta.get(OFFLINE_META_KEYS.dataKeyMarker));
}

async function classifyNative(markerRow: OfflineMetaRow | undefined, stored: NativeRawRead): Promise<NativeStored> {
  const raw = stored instanceof Uint8Array ? stored : null;
  const keyId = raw ? await fingerprint(raw) : null;
  if (markerRow === undefined) {
    return raw && keyId ? { kind: 'unmarked', raw, keyId } : { kind: 'empty' };
  }
  const marker = parseDataKeyMarker(markerRow.value);
  // An unreadable marker still says a key existed: never treat it as a first run.
  if (!marker) return { kind: 'lost', custody: 'native' };
  if (marker.custody !== 'native') return { kind: 'lost', custody: marker.custody };
  if (!raw || keyId !== marker.keyId) return { kind: 'lost', custody: 'native' };
  return { kind: 'ready', raw, keyId };
}

function decodeRawKey(text: string): Uint8Array | null {
  if (!NATIVE_RAW_KEY_RE.test(text)) return null;
  const bytes = base64UrlToBytes(text);
  return bytes && bytes.byteLength === RAW_KEY_BYTES ? bytes : null;
}

/** The native key id: base64url(SHA-256(raw)), first 16 characters. */
async function fingerprint(raw: Uint8Array): Promise<string> {
  const digest = new Uint8Array(await crypto.subtle.digest('SHA-256', raw));
  return bytesToBase64Url(digest).slice(0, 16);
}

/* ── Shared ────────────────────────────────────────────────────────────── */

/** The marker that vouches for `keyId` — read back by `parseDataKeyMarker()` (`db.ts`). */
function markerFor(keyId: string, custody: DataKeyCustody): DataKeyMarker {
  return { keyId, custody };
}

/** A key is handed out only after it has encrypted and decrypted a probe. */
async function keyWorks(key: CryptoKey): Promise<boolean> {
  try {
    const iv = crypto.getRandomValues(new Uint8Array(12));
    const aad = new TextEncoder().encode(KEY_CHECK_AAD);
    const probe = new Uint8Array([0x73, 0x79, 0x6e, 0x74, 0x72, 0x79]);
    const sealed = await crypto.subtle.encrypt({ name: 'AES-GCM', iv, additionalData: aad }, key, probe);
    const opened = new Uint8Array(await crypto.subtle.decrypt({ name: 'AES-GCM', iv, additionalData: aad }, key, sealed));
    return opened.length === probe.length && opened.every((byte, index) => byte === probe[index]);
  } catch {
    // The key cannot encrypt or decrypt (wrong algorithm or usages, or a broken
    // platform key): not usable as the data key.
    return false;
  }
}

function hasWebCrypto(): boolean {
  return (
    typeof crypto !== 'undefined' &&
    crypto !== null &&
    typeof crypto.getRandomValues === 'function' &&
    typeof crypto.subtle === 'object' &&
    crypto.subtle !== null
  );
}

function ready(key: CryptoKey, keyId: string, custody: DataKeyCustody): ReadyState {
  return { status: 'ready', key, keyId, custody };
}

function unavailable(reason: UnavailableReason): DataKeyState {
  return { status: 'unavailable', reason };
}

function bytesToBase64Url(bytes: Uint8Array): string {
  let binary = '';
  for (let index = 0; index < bytes.length; index += 1) {
    binary += String.fromCharCode(bytes[index] ?? 0);
  }
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

function base64UrlToBytes(text: string): Uint8Array | null {
  if (!/^[A-Za-z0-9_-]*$/.test(text) || text.length % 4 === 1) return null;
  try {
    const binary = atob(text.replace(/-/g, '+').replace(/_/g, '/') + '='.repeat((4 - (text.length % 4)) % 4));
    const bytes = new Uint8Array(binary.length);
    for (let index = 0; index < binary.length; index += 1) {
      bytes[index] = binary.charCodeAt(index);
    }
    return bytes;
  } catch {
    // Not decodable as base64: a malformed stored key.
    return null;
  }
}

function errorName(err: unknown): string {
  if (typeof err === 'object' && err !== null) {
    const name: unknown = Reflect.get(err, 'name');
    if (typeof name === 'string' && name.length > 0) return name;
  }
  return 'UnknownError';
}
